import React, { useState, useEffect } from "react";
import {
  ArrowLeft,
  Heart,
  Bell,
  Share2,
  Info,
  ChevronDown,
  Clock,
  ChevronRight,
  Edit2,
  Check,
  X,
} from "lucide-react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  TimeScale,
} from "chart.js";
import "chartjs-adapter-date-fns";
import zoomPlugin from "chartjs-plugin-zoom";

import ShareholdingDashboard from "@/admincompanyinfo/company_info/shareholdingDashboard_component";
import apiService from "@/services/api/api.service";
import BalanceSheet from "@/admincompanyinfo/company_info/balanceSheet_component";
import CompanyInformation from "@/admincompanyinfo/company_info/company_informatio_component";
import CompanyOverview from "@/admincompanyinfo/company_info/company_overview_component";
import FinancialData from "@/admincompanyinfo/company_info/financial_data_component";
import News from "@/admincompanyinfo/company_info/news_component";
import CorporateActions from "@/admincompanyinfo/company_info/corporateActions_component";
import PriceInfo from "@/admincompanyinfo/company_info/price_component";

import { useWatchlist } from "@/services/contextAPi/watchlistContext";
import adminProductService from "@/services/api/admin.product.service";

ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  zoomPlugin
);

const ProductHistory = ({ uniqueTitle, onBackClick }) => {
  const [companyData, setCompanyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("price");
  const [chartTimeRange, setChartTimeRange] = useState("1Y");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [priceHistoryData, setPriceHistoryData] = useState({});
  const [chartLoading, setChartLoading] = useState(false);
  const {
    watchlists,
    isProductInWatchlist,
    removeFromWatchlist,
    loading: watchlistLoading,
    error: watchlistError,
    fetchWatchlists,
  } = useWatchlist();

  useEffect(() => {
    // Check if user is admin
    const checkAdminStatus = async () => {
      try {
        const userInfo = await apiService.getUserInfo();
        setIsAdmin(userInfo?.role === "admin");
      } catch (err) {
        console.error("Failed to check admin status:", err);
        setIsAdmin(false);
      }
    };

    checkAdminStatus();
  }, []);
  useEffect(() => {
    const fetchPriceHistory = async () => {
      if (!companyData?._id) return;

      setChartLoading(true);
      try {
        // Map frontend time ranges to backend snapshot types
        const snapshotMapping = {
          "1M": "1m",
          "3M": "1m", // Use monthly data for 3M
          "6M": "1m", // Use monthly data for 6M
          "1Y": "1y",
          All: "10y",
        };

        const snapshotType = snapshotMapping[chartTimeRange] || "1m";
        const limit = chartTimeRange === "All" ? 500 : 100;

        const response = await adminProductService.getPriceHistory(
          companyData._id,
          snapshotType,
          limit
        );

        if (response.success && response.data) {
          setPriceHistoryData((prev) => ({
            ...prev,
            [chartTimeRange]: response.data,
          }));
        }
      } catch (error) {
        console.error("Error fetching price history:", error);
        // Fallback to existing chartData if available
      } finally {
        setChartLoading(false);
      }
    };

    fetchPriceHistory();
  }, [companyData?._id, chartTimeRange]);
  // Add this effect to refresh all company data when switching tabs
  useEffect(() => {
    if (uniqueTitle && activeTab) {
      // Refresh data when switching tabs
      const refreshProductData = async () => {
        try {
          const response = await apiService.getProductByTitle(uniqueTitle);
          if (response?.success && response?.data) {
            setCompanyData(response.data);
          }
        } catch (error) {
          console.error("Failed to refresh company data:", error);
        }
      };

      refreshProductData();
    }
  }, [activeTab, uniqueTitle]);

  useEffect(() => {
    if (!uniqueTitle) {
      setError("No company identifier provided");
      setLoading(false);
      return;
    }

    const fetchCompanyData = async () => {
      try {
        setLoading(true);
        const response = await apiService.getProductByTitle(uniqueTitle);

        if (response && response.success && response.data) {
          const data = response.data;

          if (!data.chartData || data.chartData.length === 0) {
            const today = new Date();
            const sampleChartData = [];
            let basePrice = data.weekData52?.buyPrice || 1000;

            for (let i = 0; i < 12; i++) {
              const date = new Date(today);
              date.setMonth(today.getMonth() - i);
              const fluctuation = Math.random() * 0.2 - 0.1;
              const price = basePrice * (1 + fluctuation);

              sampleChartData.unshift({
                date: date.toISOString().split("T")[0],
                price: price,
              });
              basePrice = price;
            }
            data.chartData = sampleChartData;
          }

          setCompanyData(data);
        } else {
          setError("No data found for this company");
        }
      } catch (err) {
        setError(
          err.message || "An error occurred while fetching company data"
        );
        console.error("API Error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchCompanyData();
    fetchWatchlists();
  }, [uniqueTitle, fetchWatchlists]);

  // This function will be passed as a prop to PriceInfo component
  const handlePriceUpdate = (updatedItem) => {
    // Update the companyData state with the new price information
    setCompanyData((prevData) => {
      if (!prevData) return updatedItem;

      return {
        ...prevData,
        weekData52: updatedItem.weekData52,
        allTime: updatedItem.allTime,
      };
    });
  };

  // New function to handle company info updates
  const handleCompanyInfoUpdate = (updatedInfo) => {
  
    // Update the companyData state with the new company information
    setCompanyData((prevData) => {
      if (!prevData) return { basicData: updatedInfo };

      return {
        ...prevData,
        basicData: updatedInfo,
      };
    });

    // Refresh the entire company data to ensure all components have the latest data
    const refreshAllData = async () => {
      try {
        const response = await apiService.getProductByTitle(uniqueTitle);
        if (response?.success && response?.data) {
          setCompanyData(response.data);
        }
      } catch (error) {
        console.error("Failed to refresh company data after update:", error);
      }
    };

    // Small delay to ensure the API has processed the update
    setTimeout(refreshAllData, 500);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const formatNumber = (num) => {
    if (num === undefined || num === null) return "N/A";
    return num.toLocaleString("en-IN");
  };

  const formatCurrency = (value) => {
    if (value === undefined || value === null) return "N/A";
    if (value >= 10000000) return `₹${(value / 10000000).toFixed(2)} Cr`;
    if (value >= 100000) return `₹${(value / 100000).toFixed(2)} Lakh`;
    return `₹${formatNumber(value)}`;
  };

  const calculateChange = (current, previous) => {
    if (!current || !previous) return { value: "N/A", isPositive: null };
    const diff = current - previous;
    const percentage = (diff / previous) * 100;
    return {
      value: `${percentage.toFixed(2)}%`,
      isPositive: diff >= 0,
    };
  };

  const prepareChartData = () => {
    const historyData = priceHistoryData[chartTimeRange];
    let dataToUse = [];

    if (historyData && historyData.length > 0) {
      // Use price history data
      dataToUse = historyData.map((item) => ({
        date: item.date,
        price: item.priceData.buyPrice || item.priceData.sellPrice || 0,
      }));
    } else if (companyData?.chartData && companyData.chartData.length > 0) {
      // Fallback to existing chartData with time filtering
      const now = new Date();
      let startDate;

      switch (chartTimeRange) {
        case "1M":
          startDate = new Date(now.getTime());
          startDate.setMonth(startDate.getMonth() - 1);
          break;
        case "3M":
          startDate = new Date(now.getTime());
          startDate.setMonth(startDate.getMonth() - 3);
          break;
        case "6M":
          startDate = new Date(now.getTime());
          startDate.setMonth(startDate.getMonth() - 6);
          break;
        case "1Y":
          startDate = new Date(now.getTime());
          startDate.setFullYear(startDate.getFullYear() - 1);
          break;
        case "All":
        default:
          startDate = new Date(0);
          break;
      }

      const sortedData = [...companyData.chartData].sort(
        (a, b) => new Date(a.date) - new Date(b.date)
      );

      dataToUse = sortedData
        .filter((point) => new Date(point.date) >= startDate)
        .map((point) => ({
          date: point.date,
          price: point.price,
        }));
    }

    if (dataToUse.length === 0) {
      return {
        labels: [],
        datasets: [],
      };
    }

    // Sort data by date
    dataToUse.sort((a, b) => new Date(a.date) - new Date(b.date));

    return {
      labels: dataToUse.map((point) => new Date(point.date)),
      datasets: [
        {
          label: "Price (₹)",
          data: dataToUse.map((point) => point.price),
          borderColor: "#3b82f6",
          backgroundColor: "rgba(59, 130, 246, 0.2)",
          fill: true,
          tension: 0.1,
          pointRadius: dataToUse.length > 50 ? 0 : 2, // Hide points for large datasets
          pointHoverRadius: 4,
        },
      ],
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        display: true,
      },
      tooltip: {
        enabled: true,
        mode: "index",
        intersect: false,
        callbacks: {
          title: (context) => {
            const date = new Date(context[0].parsed.x);
            return date.toLocaleDateString("en-IN", {
              year: "numeric",
              month: "short",
              day: "numeric",
            });
          },
          label: (context) => `Price: ₹${formatNumber(context.parsed.y)}`,
        },
      },
      zoom: {
        zoom: {
          wheel: { enabled: true },
          pinch: { enabled: true },
          mode: "xy",
        },
        pan: {
          enabled: true,
          mode: "xy",
        },
      },
    },
    scales: {
      x: {
        type: "time",
        time: {
          unit:
            chartTimeRange === "All"
              ? "year"
              : chartTimeRange === "1Y"
              ? "month"
              : "day",
        },
        title: { display: true, text: "Date" },
        grid: {
          display: true,
          color: "rgba(0, 0, 0, 0.1)",
        },
      },
      y: {
        title: { display: true, text: "Price (₹)" },
        beginAtZero: false,
        grid: {
          display: true,
          color: "rgba(0, 0, 0, 0.1)",
        },
        ticks: {
          callback: function (value) {
            return "₹" + formatNumber(value);
          },
        },
      },
    },
    interaction: {
      intersect: false,
      mode: "index",
    },
  };

  if (loading)
    return (
      <div className="loading-spinner flex items-center justify-center min-h-screen bg-gray-50 mx-auto w-full sm:w-[90%] px-4">
        <Clock className="animate-spin mr-2" size={24} /> Loading company
        data...
      </div>
    );

  if (error)
    return (
      <div className="error-message flex flex-col items-center justify-center min-h-screen bg-gray-50 mx-auto w-full sm:w-[90%] px-4">
        <div className="bg-red-100 text-red-700 p-4 rounded-lg max-w-md text-center w-full">
          <p className="font-medium">Error</p>
          <p>{error}</p>
          <button
            className="mt-4 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
            onClick={onBackClick}
          >
            Go to Product List
          </button>
        </div>
      </div>
    );

  if (!companyData)
    return (
      <div className="no-data flex items-center justify-center min-h-screen bg-gray-50 mx-auto w-full sm:w-[90%] px-4">
        <div className="bg-blue-100 text-blue-700 p-4 rounded-lg max-w-md text-center w-full">
          <p>No data found for this company</p>
          <button
            className="mt-4 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
            onClick={onBackClick}
          >
            Go to Product List
          </button>
        </div>
      </div>
    );

  const { basicData = {}, weekData52 = {}, financials = {} } = companyData;

  const {
    legalName = "Company Name Unavailable",
    uniqueTitle: companyFullSlug = uniqueTitle,
    listingStatus = "Status Unavailable",
  } = basicData;

  const companySlug = companyFullSlug.split("-")[0];

  const { buyPrice, sellPrice, lastUpdate, previousClose } = weekData52;

  const priceChange = calculateChange(buyPrice, previousClose);
  const isInWatchlist =
    companyData?._id && isProductInWatchlist(companyData._id);

  return (
    <div className="company-page min-h-screen  mt-16 mt-[1%]">
      <div className="mx-auto w-full sm:w-[90%] px-4 sm:px-0">
        <div className="bg-white shadow-sm top-0 z-10 w-full">
          <div className="mx-auto px-4 py-6 w-full">
            <div className="flex items-center justify-between mb-4 flex-col sm:flex-row">
              <button
                className="flex items-center text-gray-600 hover:text-blue-600 text-sm bg-gray-100 hover:bg-blue-100 rounded-full px-3 py-1 transition-colors duration-200 mb-2 sm:mb-0"
                onClick={onBackClick}
              >
                <ArrowLeft size={16} className="mr-1" /> Back to Product List
              </button>
            </div>

            <div className="flex items-center justify-between flex-col sm:flex-row gap-4">
              <div className="flex items-center space-x-4 w-full sm:w-[95%] sm:ml-[5%]">
                <div className="company-logo w-10 h-10 bg-gray-200 rounded-md flex items-center justify-center flex-shrink-0">
                  {legalName.charAt(0)}
                </div>
                <div className="ml-0 sm:ml-6">
                  <h1 className="text-lg sm:text-xl font-semibold">
                    {legalName}
                  </h1>
                  <div className="flex items-center text-xs sm:text-sm text-gray-500 flex-wrap gap-2">
                    <span className="mr-2 sm:mr-5">{companySlug}</span>
                    <span
                      className={`inline-block w-2 h-2 rounded-full ${
                        listingStatus.toLowerCase() === "active"
                          ? "bg-green-500"
                          : "bg-red-500"
                      } mr-1`}
                    ></span>
                    <span>{listingStatus}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-4 border-b border-gray-200">
              <nav className="flex overflow-x-auto whitespace-nowrap">
                <button
                  onClick={() => setActiveTab("price")}
                  className={`px-2 sm:px-4 py-3 text-xs sm:text-sm font-medium border-b-2 whitespace-nowrap ${
                    activeTab === "price"
                      ? "border-blue-600 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                >
                  Price
                </button>
                <button
                  onClick={() => setActiveTab("overview")}
                  className={`px-2 sm:px-4 py-3 text-xs sm:text-sm font-medium border-b-2 whitespace-nowrap ${
                    activeTab === "overview"
                      ? "border-blue-600 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                >
                  Overview
                </button>
                <button
                  onClick={() => setActiveTab("financials")}
                  className={`px-2 sm:px-4 py-3 text-xs sm:text-sm font-medium border-b-2 whitespace-nowrap ${
                    activeTab === "financials"
                      ? "border-blue-600 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                >
                  Financials
                </button>
                <button
                  onClick={() => setActiveTab("balanceSheet")}
                  className={`px-2 sm:px-4 py-3 text-xs sm:text-sm font-medium border-b-2 whitespace-nowrap ${
                    activeTab === "balanceSheet"
                      ? "border-blue-600 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                >
                  Balance Sheet
                </button>
                <button
                  onClick={() => setActiveTab("news")}
                  className={`px-2 sm:px-4 py-3 text-xs sm:text-sm font-medium border-b-2 whitespace-nowrap ${
                    activeTab === "news"
                      ? "border-blue-600 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                >
                  News
                </button>
                <button
                  onClick={() => setActiveTab("analysis")}
                  className={`px-2 sm:px-4 py-3 text-xs sm:text-sm font-medium border-b-2 whitespace-nowrap ${
                    activeTab === "analysis"
                      ? "border-blue-600 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                >
                  Analysis
                </button>
                <button
                  onClick={() => setActiveTab("shareholding")}
                  className={`px-2 sm:px-4 py-3 text-xs sm:text-sm font-medium border-b-2 whitespace-nowrap ${
                    activeTab === "shareholding"
                      ? "border-blue-600 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                >
                  Shareholding
                </button>
                <button
                  onClick={() => setActiveTab("priceChart")}
                  className={`px-2 sm:px-4 py-3 text-xs sm:text-sm font-medium border-b-2 whitespace-nowrap ${
                    activeTab === "priceChart"
                      ? "border-blue-600 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                >
                  Price Chart
                </button>
                <button
                  onClick={() => setActiveTab("companyInfo")}
                  className={`px-2 sm:px-4 py-3 text-xs sm:text-sm font-medium border-b-2 whitespace-nowrap ${
                    activeTab === "companyInfo"
                      ? "border-blue-600 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                >
                  Company Information
                </button>
                <button
                  onClick={() => setActiveTab("corporate actions")}
                  className={`px-2 sm:px-4 py-3 text-xs sm:text-sm font-medium border-b-2 whitespace-nowrap ${
                    activeTab === "corporate actions"
                      ? "border-blue-600 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                >
                  Corporate Actions
                </button>
              </nav>
            </div>
          </div>
        </div>

        <div className="mx-auto px-4 py-6 w-full">
          {activeTab === "price" && (
            <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
              <PriceInfo
                uniqueTitle={uniqueTitle}
                onPriceUpdate={handlePriceUpdate}
              />
            </div>
          )}

          {activeTab === "overview" && (
            <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
              <CompanyOverview uniqueTitle={uniqueTitle} />
            </div>
          )}

          {activeTab === "balanceSheet" && (
            <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
              <BalanceSheet uniqueTitle={uniqueTitle} />
            </div>
          )}

          {activeTab === "financials" && (
            <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
              <FinancialData uniqueTitle={uniqueTitle} />
            </div>
          )}

          {activeTab === "news" && (
            <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
              <News uniqueTitle={uniqueTitle} />
            </div>
          )}

          {activeTab === "analysis" && (
            <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
              <h3 className="text-lg font-semibold">
                No data is available for analysis
              </h3>
            </div>
          )}

          {activeTab === "shareholding" && (
            <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
              <ShareholdingDashboard uniqueTitle={uniqueTitle} />
            </div>
          )}

          {activeTab === "priceChart" && (
            <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
              <div className="flex items-center justify-between mb-4 flex-col sm:flex-row gap-2">
                <h3 className="text-lg font-semibold">
                  Price Chart
                  {chartLoading && (
                    <span className="ml-2 text-sm text-gray-500">
                      Loading...
                    </span>
                  )}
                </h3>
                <div className="flex space-x-2 flex-wrap gap-2">
                  {["1M", "3M", "6M", "1Y", "All"].map((range) => (
                    <button
                      key={range}
                      onClick={() => setChartTimeRange(range)}
                      disabled={chartLoading}
                      className={`px-3 py-1 text-xs rounded transition-colors ${
                        chartTimeRange === range
                          ? "bg-blue-100 text-blue-700 border border-blue-300"
                          : "hover:bg-gray-100 border border-gray-200"
                      } ${chartLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                    >
                      {range}
                    </button>
                  ))}
                </div>
              </div>

              {/* Price Summary */}
              {companyData?.weekData52 && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4 p-3 bg-gray-50 rounded-lg">
                  <div className="text-center">
                    <p className="text-xs text-gray-500">Buy Price</p>
                    <p className="font-semibold text-green-600">
                      ₹{formatNumber(companyData.weekData52.buyPrice)}
                    </p>
                  </div>

                  <div className="text-center">
                    <p className="text-xs text-gray-500">52W High</p>
                    <p className="font-semibold">
                      ₹{formatNumber(companyData.weekData52.high52Week)}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-gray-500">52W Low</p>
                    <p className="font-semibold">
                      ₹{formatNumber(companyData.weekData52.low52Week)}
                    </p>
                  </div>
                </div>
              )}

              <div className="h-64 sm:h-80 relative">
                {chartLoading && (
                  <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
                      <p className="text-sm text-gray-500">
                        Loading price data...
                      </p>
                    </div>
                  </div>
                )}
                <Line data={prepareChartData()} options={chartOptions} />
              </div>

              {/* Data source indicator */}
              <div className="mt-2 text-xs text-gray-500 text-right">
                {priceHistoryData[chartTimeRange]
                  ? `Price history data (${priceHistoryData[chartTimeRange].length} points)`
                  : "Using fallback chart data"}
              </div>
            </div>
          )}

          {activeTab === "companyInfo" && (
            <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
              <CompanyInformation
                uniqueTitle={uniqueTitle}
                onCompanyInfoUpdate={handleCompanyInfoUpdate}
              />
            </div>
          )}

          {activeTab === "corporate actions" && (
            <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
              <CorporateActions uniqueTitle={uniqueTitle} />
            </div>
          )}
        </div>

        {isModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
            <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 w-full max-w-md sm:max-w-3xl max-h-[90vh] overflow-hidden">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg sm:text-xl font-semibold">Watchlist</h2>
                <button
                  onClick={closeModal}
                  className="text-gray-500 hover:text-gray-700 text-xl sm:text-2xl font-bold"
                >
                  ✕
                </button>
              </div>

              <div className="overflow-y-auto" style={{ maxHeight: "75vh" }}>
                {/* You'll need to have a Watchlist component defined somewhere */}
                {/* <Watchlist productId={companyData?._id} /> */}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductHistory;
